export function LegalConsiderations() {
  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Legal Considerations When Selling Apartment Buildings
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Understanding legal requirements and protecting yourself legally are critical tips for selling an 
            apartment building. Commercial real estate transactions involve complex legal issues that can derail 
            deals or create liability if not handled properly. Work with experienced professionals and understand 
            these key legal considerations.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
          <div>
            <h3 className="text-3xl font-bold text-gray-900 mb-6">Essential Legal Documentation</h3>
            <p className="text-gray-600 mb-6">
              Proper legal documentation protects both parties and ensures smooth transactions. Missing or 
              incomplete documentation is one of the most common reasons apartment building sales fall through 
              or result in post-closing disputes. Prepare these documents carefully with qualified legal counsel.
            </p>
            <div className="space-y-4">
              <div className="bg-gray-50 p-5 rounded-lg border-l-4 border-green-500">
                <h4 className="font-semibold text-gray-900 mb-2 flex items-center">
                  <i className="ri-file-text-line text-green-600 mr-2"></i>
                  Purchase and Sale Agreement
                </h4>
                <p className="text-gray-600 text-sm">
                  The PSA is the foundation of your transaction. It should clearly define purchase price, 
                  earnest money, contingencies, closing date, included personal property, prorations, and 
                  all other material terms. Use attorney-drafted agreements specific to commercial real estate, 
                  not residential forms.
                </p>
              </div>
              <div className="bg-gray-50 p-5 rounded-lg border-l-4 border-orange-500">
                <h4 className="font-semibold text-gray-900 mb-2 flex items-center">
                  <i className="ri-shield-check-line text-orange-600 mr-2"></i>
                  Title Insurance and Survey
                </h4>
                <p className="text-gray-600 text-sm">
                  Obtain title insurance to protect against title defects and provide a current survey showing 
                  property boundaries, easements, and encroachments. Address any title issues before closing 
                  to avoid delays or complications.
                </p>
              </div>
              <div className="bg-gray-50 p-5 rounded-lg border-l-4 border-red-500">
                <h4 className="font-semibold text-gray-900 mb-2 flex items-center">
                  <i className="ri-building-line text-red-600 mr-2"></i>
                  Lease Assignment Documents
                </h4>
                <p className="text-gray-600 text-sm">
                  Prepare proper lease assignment documents transferring all tenant leases to the buyer. 
                  Include estoppel certificates from tenants confirming lease terms, security deposits, 
                  and any agreements or modifications.
                </p>
              </div>
              <div className="bg-gray-50 p-5 rounded-lg border-l-4 border-indigo-500">
                <h4 className="font-semibold text-gray-900 mb-2 flex items-center">
                  <i className="ri-alert-line text-indigo-600 mr-2"></i>
                  Disclosure Documents
                </h4>
                <p className="text-gray-600 text-sm">
                  Provide comprehensive disclosures about property condition, known defects, environmental 
                  issues, legal disputes, code violations, and material facts. Proper disclosure protects 
                  you from post-closing liability claims.
                </p>
              </div>
            </div>
          </div>
          <div>
            <img 
              src="https://public.readdy.ai/ai/img_res/b1d8992c60998c3f2d83553da84a0ea1.jpg"
              alt="Legal documentation review"
              className="w-full h-96 object-cover rounded-xl shadow-lg"
            />
          </div>
        </div>

        <div className="bg-gray-50 rounded-xl p-12 mb-16">
          <h3 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            Tenant Rights and Lease Transfers
          </h3>
          <p className="text-gray-600 text-center mb-12 max-w-3xl mx-auto">
            One of the most important legal tips for selling an apartment building involves properly handling 
            tenant rights and lease transfers. Tenants have legal protections that must be respected, and 
            improper handling can create liability or derail your sale.
          </p>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-user-follow-line text-2xl text-green-600"></i>
              </div>
              <h4 className="text-lg font-semibold text-gray-900 mb-3">Tenant Notifications</h4>
              <p className="text-gray-600 text-sm mb-4">
                Most states require notifying tenants of ownership changes. Provide written notice including 
                new owner information, where to send rent, and security deposit transfer details.
              </p>
              <ul className="text-gray-600 text-xs space-y-2">
                <li>• Check state-specific notification requirements</li>
                <li>• Provide notice in writing with proper timing</li>
                <li>• Include new owner contact information</li>
                <li>• Document all tenant communications</li>
              </ul>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-safe-line text-2xl text-orange-600"></i>
              </div>
              <h4 className="text-lg font-semibold text-gray-900 mb-3">Security Deposit Transfers</h4>
              <p className="text-gray-600 text-sm mb-4">
                Security deposits must be properly transferred to the new owner with complete accounting. 
                Failure to handle deposits correctly can create liability for both parties.
              </p>
              <ul className="text-gray-600 text-xs space-y-2">
                <li>• Provide detailed security deposit accounting</li>
                <li>• Transfer actual funds at closing</li>
                <li>• Document condition of units at transfer</li>
                <li>• Notify tenants of deposit transfer</li>
              </ul>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-file-shield-line text-2xl text-red-600"></i>
              </div>
              <h4 className="text-lg font-semibold text-gray-900 mb-3">Lease Obligations</h4>
              <p className="text-gray-600 text-sm mb-4">
                All existing lease terms remain in effect after sale. The new owner assumes all obligations 
                including maintenance responsibilities, renewal options, and any special agreements.
              </p>
              <ul className="text-gray-600 text-xs space-y-2">
                <li>• Provide complete copies of all leases</li>
                <li>• Disclose any lease modifications or side agreements</li>
                <li>• Transfer maintenance and service contracts</li>
                <li>• Document any tenant disputes or issues</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <img 
              src="https://readdy.ai/api/search-image?query=environmental%20inspection%20of%20apartment%20building%20professional%20inspector%20checking%20for%20hazards%20testing%20equipment%20environmental%20compliance%20assessment%20commercial%20property%20due%20diligence%20safety%20evaluation&width=800&height=600&seq=tips-legal-002&orientation=landscape"
              alt="Environmental inspection"
              className="w-full h-96 object-cover rounded-xl shadow-lg"
            />
          </div>
          <div>
            <h3 className="text-3xl font-bold text-gray-900 mb-6">Environmental Compliance and Liability</h3>
            <p className="text-gray-600 mb-6">
              Environmental issues can create significant liability and are among the most serious legal 
              considerations when selling apartment buildings. Understanding and addressing environmental 
              concerns protects you from future liability and prevents deal complications.
            </p>
            <div className="space-y-4">
              <div className="bg-white p-5 rounded-lg shadow-md">
                <h4 className="font-semibold text-gray-900 mb-2 flex items-center">
                  <i className="ri-flask-line text-orange-500 mr-2"></i>
                  Phase I Environmental Assessment
                </h4>
                <p className="text-gray-600 text-sm">
                  Most buyers require Phase I environmental assessments to identify potential contamination 
                  or environmental hazards. Consider obtaining this report proactively to address any issues 
                  before listing and demonstrate transparency to buyers.
                </p>
              </div>
              <div className="bg-white p-5 rounded-lg shadow-md">
                <h4 className="font-semibold text-gray-900 mb-2 flex items-center">
                  <i className="ri-leaf-line text-green-500 mr-2"></i>
                  Common Environmental Concerns
                </h4>
                <ul className="text-gray-600 text-sm space-y-1">
                  <li>• Asbestos in older buildings (pre-1980)</li>
                  <li>• Lead-based paint in units built before 1978</li>
                  <li>• Underground storage tanks (current or historical)</li>
                  <li>• Mold or water damage issues</li>
                  <li>• Soil or groundwater contamination</li>
                  <li>• Radon in certain geographic areas</li>
                </ul>
              </div>
              <div className="bg-white p-5 rounded-lg shadow-md">
                <h4 className="font-semibold text-gray-900 mb-2 flex items-center">
                  <i className="ri-shield-cross-line text-red-500 mr-2"></i>
                  Liability Protection
                </h4>
                <p className="text-gray-600 text-sm">
                  Obtain environmental insurance or negotiate liability caps in your purchase agreement. 
                  Proper disclosure and documentation of environmental conditions provides important legal 
                  protection against future claims.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}